import { VariableKind } from '../constants'
import type { TemplateVariable } from '../types'
import { MonikerKind } from '../cdm/types'
import type { CollectedMoniker } from '../cdm/collectMonikers'
import { normalizeMonikerKey } from '../cdm/monikerKey'


// Build a nested object skeleton from a flat list of normalized key paths.
// Scalars → ""; loops → [{}]; conditionals → false.
function buildSkeleton(variables: TemplateVariable[]): Record<string, unknown> {
  const root: Record<string, unknown> = {}

  for (const v of variables) {
    const segments = v.normalizedKey.split('.')
    let cursor: Record<string, unknown> = root

    for (let i = 0; i < segments.length - 1; i++) {
      const seg = segments[i]
      if (!(seg in cursor) || typeof cursor[seg] !== 'object' || cursor[seg] === null || Array.isArray(cursor[seg])) {
        cursor[seg] = {}
      }
      cursor = cursor[seg] as Record<string, unknown>
    }

    const leaf = segments[segments.length - 1]
    if (!(leaf in cursor)) {
      if (v.variableKindLookupId === VariableKind.LOOP) {
        cursor[leaf] = [{}]
      } else if (v.variableKindLookupId === VariableKind.CONDITIONAL) {
        cursor[leaf] = false
      } else {
        cursor[leaf] = ''
      }
    }
  }

  return root
}

export function buildJsonSkeleton(variables: TemplateVariable[]): string {
  return JSON.stringify(buildSkeleton(variables), null, 2)
}

function objectToXml(obj: unknown, tagName: string, indent: string): string {
  if (Array.isArray(obj)) {
    return obj.map(item => objectToXml(item, tagName, indent)).join('\n')
  }
  if (typeof obj === 'object' && obj !== null) {
    const inner = Object.entries(obj as Record<string, unknown>)
      .map(([k, v]) => objectToXml(v, k, indent + '  '))
      .join('\n')
    return `${indent}<${tagName}>\n${inner}\n${indent}</${tagName}>`
  }
  return `${indent}<${tagName}>${String(obj ?? '')}</${tagName}>`
}

export function buildXmlSkeleton(variables: TemplateVariable[]): string {
  const root = buildSkeleton(variables)
  const inner = Object.entries(root)
    .map(([k, v]) => objectToXml(v, k, '  '))
    .join('\n')
  return `<data>\n${inner}\n</data>`
}

// Extract a flat map of { normalizedKey -> value } from a JSON/XML payload string.
// Used to pre-populate the friendly form from a raw payload.
export function extractValuesFromJson(payload: string): Record<string, string> {
  try {
    const obj = JSON.parse(payload)
    return flattenObject(obj, '')
  } catch {
    return {}
  }
}

function flattenObject(obj: unknown, prefix: string): Record<string, string> {
  const result: Record<string, string> = {}
  if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${k}` : k
      if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
        Object.assign(result, flattenObject(v, path))
      } else {
        result[path] = String(v ?? '')
      }
    }
  }
  return result
}

// Set a dot-path value in a JSON payload string.
export function setValueInJson(payload: string, normalizedKey: string, value: string): string {
  try {
    const obj = JSON.parse(payload)
    setNestedValue(obj, normalizedKey.split('.'), value)
    return JSON.stringify(obj, null, 2)
  } catch {
    return payload
  }
}

function setNestedValue(obj: Record<string, unknown>, segments: string[], value: string): void {
  const [head, ...rest] = segments
  if (rest.length === 0) {
    obj[head] = value
  } else {
    if (typeof obj[head] !== 'object' || obj[head] === null || Array.isArray(obj[head])) {
      obj[head] = {}
    }
    setNestedValue(obj[head] as Record<string, unknown>, rest, value)
  }
}

// ── Loop-aware form state utilities ──────────────────────────────────────────

export interface LoopRow {
  // key: normalizedKey of the sub-field variable (e.g. "item.description")
  // value: the string the user typed
  [normalizedKey: string]: string
}

/** Build an empty row for a given loop, populated with '' for all sub-fields. */
export function emptyLoopRow(subFields: TemplateVariable[]): LoopRow {
  return Object.fromEntries(subFields.map(sf => [sf.normalizedKey, '']))
}

/**
 * Build a JSON payload string from structured form state:
 *   scalarValues: normalizedKey → value (top-level scalars only)
 *   conditionalValues: normalizedKey → 'true'|'false'
 *   loopRows: loopKey → array of LoopRow objects
 */
export function buildPayloadFromForm(
  variables: TemplateVariable[],
  scalarValues: Record<string, string>,
  conditionalValues: Record<string, boolean>,
  loopRows: Record<string, LoopRow[]>,
): string {
  const obj: Record<string, unknown> = {}

  // Top-level scalars (not loop sub-fields)
  for (const v of variables) {
    if (v.variableKindLookupId === VariableKind.SCALAR && !v.loopKey) {
      const segs = v.normalizedKey.split('.')
      setNestedValue(obj, segs, scalarValues[v.normalizedKey] ?? '')
    }
    if (v.variableKindLookupId === VariableKind.CONDITIONAL) {
      obj[v.normalizedKey] = conditionalValues[v.normalizedKey] ?? false
    }
  }

  // Loop sections
  for (const v of variables) {
    if (v.variableKindLookupId !== VariableKind.LOOP) continue
    const subFields = variables.filter(sf => sf.loopKey === v.normalizedKey)
    const rows = loopRows[v.normalizedKey] ?? []
    obj[v.normalizedKey] = rows.map(row => {
      const item: Record<string, string> = {}
      for (const sf of subFields) {
        // Strip the alias prefix (everything up to and including first dot)
        const dotIdx = sf.normalizedKey.indexOf('.')
        const fieldName = dotIdx >= 0 ? sf.normalizedKey.slice(dotIdx + 1) : sf.normalizedKey
        item[fieldName] = row[sf.normalizedKey] ?? ''
      }
      return item
    })
  }

  return JSON.stringify(obj, null, 2)
}

/** Parse loop rows back from a JSON string (for sync when user edits raw mode). */
export function extractLoopRowsFromJson(
  payload: string,
  variables: TemplateVariable[],
): Record<string, LoopRow[]> {
  const result: Record<string, LoopRow[]> = {}
  try {
    const obj = JSON.parse(payload) as Record<string, unknown>
    const loopVars = variables.filter(v => v.variableKindLookupId === VariableKind.LOOP)
    for (const lv of loopVars) {
      const arr = obj[lv.normalizedKey]
      if (!Array.isArray(arr)) continue
      const subFields = variables.filter(sf => sf.loopKey === lv.normalizedKey)
      result[lv.normalizedKey] = arr.map(item => {
        const row: LoopRow = {}
        for (const sf of subFields) {
          const dotIdx = sf.normalizedKey.indexOf('.')
          const fieldName = dotIdx >= 0 ? sf.normalizedKey.slice(dotIdx + 1) : sf.normalizedKey
          row[sf.normalizedKey] = String((item as Record<string, unknown>)[fieldName] ?? '')
        }
        return row
      })
    }
  } catch {
    // leave empty
  }
  return result
}


/**
 * A live-document moniker in the shape buildSkeleton already understands, so the editor's payload
 * panel and the preview page's produce the same JSON for the same template rather than two
 * near-identical skeleton builders drifting apart.
 */
function asVariable(moniker: CollectedMoniker): TemplateVariable {
  return {
    normalizedKey: moniker.normalizedKey,
    rawName: moniker.rawName,
    variableKindLookupId:
      moniker.kind === MonikerKind.Loop ? VariableKind.LOOP
      : moniker.kind === MonikerKind.Conditional ? VariableKind.CONDITIONAL
      : VariableKind.SCALAR,
    occurrenceCount: 1,
    contextSnippet: null,
    loopKey: null,
  }
}

/** Recursively copies keys of `skeleton` that `target` does not already have. Returns true if it added any. */
function addMissing(target: Record<string, unknown>, skeleton: Record<string, unknown>): boolean {
  let added = false
  for (const [key, value] of Object.entries(skeleton)) {
    // Skeleton keys are normalized; the payload's own are matched the way the renderer matches
    // them (MonikerKey.Normalize), so a sample's `invoiceNumber` is not joined by an
    // `invoicenumber` the API would then reject as a duplicate key.
    const existing = key in target ? key : Object.keys(target).find(k => normalizeMonikerKey(k) === key)
    if (existing === undefined) {
      target[key] = value
      added = true
    } else if (
      value !== null && typeof value === 'object' && !Array.isArray(value) &&
      target[existing] !== null && typeof target[existing] === 'object' && !Array.isArray(target[existing])
    ) {
      // Both sides are objects, so descend rather than replace — a nested key the user has
      // already filled in must survive a sibling being added next to it.
      if (addMissing(target[existing] as Record<string, unknown>, value as Record<string, unknown>)) added = true
    }
  }
  return added
}

/**
 * The preview payload with a slot for every variable now in the document, keeping whatever the
 * user has already typed.
 *
 * Strictly additive, and that is the point. The editor calls this as the document changes, so it
 * must never take anything away: values the user filled in stay, keys they added by hand stay even
 * when no variable matches them, and a variable they deleted from the document leaves its key
 * behind rather than having its data silently removed. Only missing keys are added.
 *
 * Returns `existingJson` **by identity** when there is nothing to add or when it is not currently
 * valid JSON. Both matter: the first keeps React from resetting the textarea's cursor on every
 * keystroke elsewhere in the editor, and the second means a half-typed payload is never
 * reformatted or destroyed under the user mid-edit.
 */
export function mergePayloadSkeleton(existingJson: string, monikers: CollectedMoniker[]): string {
  if (monikers.length === 0) return existingJson

  let parsed: unknown
  try {
    parsed = JSON.parse(existingJson)
  } catch {
    return existingJson
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return existingJson

  const target = parsed as Record<string, unknown>
  if (!addMissing(target, buildSkeleton(monikers.map(asVariable)))) return existingJson

  return JSON.stringify(target, null, 2)
}
